import React, { useRef } from 'react'
import { View, Text, TextInput, Platform, type TextInputProps } from 'react-native'
import { useKeyboardAware } from './KeyboardAwareScrollView'

interface InputProps extends TextInputProps {
  label?: string
  error?: string
}

export function Input({ label, error, ...props }: InputProps) {
  // When rendered inside a KeyboardAwareScrollView we get a scroll helper
  // from context.  We measure the wrapping View (not the TextInput itself)
  // so the label is also visible above the input after scrolling.
  const wrapperRef = useRef<View>(null)
  const kbAware = useKeyboardAware()

  function handleFocus(e: Parameters<NonNullable<TextInputProps['onFocus']>>[0]) {
    // Scroll this input's wrapper to the centre of the visible area above
    // the keyboard when inside a KeyboardAwareScrollView.
    kbAware?.onInputFocus(wrapperRef)
    props.onFocus?.(e)
  }

  return (
    <View ref={wrapperRef} className="gap-1">
      {!!label && (
        <Text className="font-stencil text-[10px] uppercase tracking-widest text-olive-up">
          {label}
        </Text>
      )}
      <TextInput
        className={`bg-paper border-2 rounded-md text-ink font-typed ${
          error ? 'border-blood' : 'border-olive'
        }`}
        placeholderTextColor="rgba(107, 91, 62, 0.6)"
        autoCapitalize="none"
        // Descender clip fix for `j`, `g`, `p`, `y`, `q` in the focused
        // state. Root cause: the body font (`font-typed`) descenders +
        // Tailwind's `text-base` class implying `lineHeight: 24`, which
        // forms a hard clipping box on iOS that the descender pokes
        // below. Bumping padding alone doesn't help — the clip is inside
        // the line box, not at the input's content edge.
        //
        // The cross-platform fix:
        //   1. Drop `text-base` from className so NativeWind doesn't
        //      implicitly inject lineHeight 24. Set fontSize via style
        //      and OMIT lineHeight — iOS then uses the font's
        //      intrinsic ascent+descent metrics, which include the
        //      full descender.
        //   2. Generous paddingVertical so the descender + iOS focus
        //      baseline shift land inside the input's content area.
        //   3. Android: includeFontPadding:false + textAlignVertical
        //      keeps the same visual rhythm and avoids Android's own
        //      extra top/bottom font reservation.
        textAlignVertical="center"
        {...props}
        onFocus={handleFocus}
        style={[
          {
            fontSize: 16,
            paddingHorizontal: 12,
            paddingVertical: 14,
            minHeight: 52,
            ...(Platform.OS === 'android' ? { includeFontPadding: false } : null),
          },
          props.style,
        ]}
      />
      {!!error && <Text className="font-typed text-blood text-xs italic">{error}</Text>}
    </View>
  )
}
